import { Dtos, dataNeededId, type SuretyAccountId } from "@inrev/common";
import { DateTime } from "luxon";
import { useRequest } from "../../../utils/request";
import { dataNeededQueryKey } from "../decisioning.keys";
import { type MutationFailureAction } from "../mutationFailure";
import { replaceById } from "../optimistic";
import { type DataNeeded } from "../types";
import { useOptimisticListMutation } from "../useOptimisticListMutation";

export type CreateDataNeededInput = Dtos.Decisioning.DataNeeded.Create.Request;

type UseCreateDataNeededOptions = {
	/** Runs with the server copy once it replaces the optimistic one, even if the caller has unmounted. */
	onCreated?: (created: DataNeeded) => void;
	/** Offered with the failure toast, e.g. reopening the sheet with what was typed. */
	failureAction?: (input: CreateDataNeededInput) => MutationFailureAction;
};

export const useCreateDataNeeded = (
	accountId: SuretyAccountId,
	{ onCreated, failureAction }: UseCreateDataNeededOptions = {},
) => {
	const { post } = useRequest();

	const mutation = useOptimisticListMutation<DataNeeded, CreateDataNeededInput, DataNeeded>({
		queryKey: dataNeededQueryKey(accountId),
		request: async (request) =>
			Dtos.Decisioning.DataNeeded.Get.Response.schema.parse(
				await post<unknown>(`/v2/admin/surety/accounts/${accountId}/data-needed`, request),
			),
		apply: (items, input, pendingId) => {
			const now = DateTime.now();
			return [
				...items,
				{
					assignee: input.assignee,
					type: input.type,
					fileTypes: input.fileTypes,
					title: input.title,
					rationale: input.rationale,
					description: input.description,
					importance: input.importance,
					proConId: input.proConId,
					id: dataNeededId.parse(pendingId),
					accountId,
					context: "",
					status: "open",
					closedReason: null,
					resolvedAt: null,
					resolvedByUserId: null,
					fulfillmentAttachmentIds: [],
					fulfillmentAnswer: null,
					createdAt: now,
					updatedAt: now,
				},
			];
		},
		affectedIds: (_input, pendingId) => [pendingId],
		failureMessage: (input) => `Couldn't request ${input.title}.`,
		failureAction,
		creates: true,
		reconcile: (items, created, _input, pendingId) => replaceById(items, pendingId, created),
		onSuccess: (created) => onCreated?.(created),
	});

	return {
		createDataNeeded: mutation.mutate,
		/** Resolves once the server confirms, so a confirmation can wait for it; rejects on failure. */
		createDataNeededAsync: mutation.mutateAsync,
	};
};
